import { StatusData } from "@fairfleet/geotab";
import { useMemo } from "react";
import { LiveDataRow } from "../models/LiveDataRow";
import { toLiveDataRow } from "../models/toLiveDataRow";
import { useGpsOdometerStatusData } from "./useGpsOdometerStatusData";
import { LAST_KNOWN_DIAGNOSTIC_IDS, useLastKnownStatusData } from "./useLastKnownStatusData";
import { useLiveDataLookups } from "./useLiveDataLookups";
import { useLiveStatusData } from "./useLiveStatusData";
import { useDayStatusData } from "@/hooks/statusData/useDayStatusData";

const NO_ROWS: LiveDataRow[] = [];

export function useLiveDataRows(
  deviceId: string | undefined,
  minutesBack: number = 5,
  refetchInterval: number = 10000,
) {
  const {
    data: statusData,
    isLoading,
    isFetching,
    isError: liveStatusDataIsError = false,
    isPlaceholderData: liveStatusDataIsPlaceholderData = false,
    dataUpdatedAt: liveStatusDataUpdatedAt = 0,
  } = useLiveStatusData(deviceId, minutesBack, refetchInterval);
  // What the checklist falls back to when the live window has nothing: the last 24 hours.
  const {
    data: fallbackStatusData,
    isLoading: fallbackStatusDataIsLoading = false,
    isError: fallbackStatusDataIsError = false,
    isPlaceholderData: fallbackStatusDataIsPlaceholderData = false,
  } = useDayStatusData(deviceId);
  const {
    data: lastKnownStatusData,
    isLoading: lastKnownLoading = false,
    isError: lastKnownStatusDataIsError = false,
  } = useLastKnownStatusData(deviceId, LAST_KNOWN_DIAGNOSTIC_IDS);
  const { data: gpsOdometerStatusData, isError: gpsOdometerStatusDataIsError = false } =
    useGpsOdometerStatusData(deviceId);

  // Sorted and deduplicated, because this array is part of the diagnostics query key. Left in
  // arrival order it changes whenever a rarely reported diagnostic enters or leaves the sliding
  // live window, and every diagnostic on the vehicle is fetched again.
  const diagnosticIds = useMemo(
    () =>
      sortedDiagnosticIds(
        statusData,
        fallbackStatusData,
        lastKnownStatusData,
        gpsOdometerStatusData,
      ),
    [statusData, fallbackStatusData, lastKnownStatusData, gpsOdometerStatusData],
  );

  const {
    lookups,
    isLoading: diagnosticsLoading,
    isError: diagnosticsIsError,
  } = useLiveDataLookups(diagnosticIds);

  // A refetch in flight is the remedy for old data, not a reason to hide it: after a hidden tab
  // or a shorter refresh interval the data is old for exactly as long as the request runs.
  const liveStatusDataIsStale =
    !isFetching &&
    liveStatusDataUpdatedAt > 0 &&
    Date.now() - liveStatusDataUpdatedAt > 2 * refetchInterval;
  const fallbackLoading =
    fallbackStatusDataIsPlaceholderData || (fallbackStatusDataIsLoading && !fallbackStatusData);
  const liveRowsDistrusted =
    liveStatusDataIsError || liveStatusDataIsPlaceholderData || liveStatusDataIsStale;
  const fallbackRowsStatusData =
    fallbackStatusDataIsError || fallbackLoading ? undefined : fallbackStatusData;

  // The grid shows what was last received; only the checklist has to distrust it, because there
  // an old reading would read as "reported now".
  const gridRows: LiveDataRow[] = useMemo(
    () => (statusData ?? []).map(sd => toLiveDataRow(sd, lookups)),
    [statusData, lookups],
  );
  const rows = useMemo(
    () => (liveRowsDistrusted ? NO_ROWS : gridRows),
    [liveRowsDistrusted, gridRows],
  );

  const fallbackRows: LiveDataRow[] = useMemo(
    () => (fallbackRowsStatusData ?? []).map(sd => toLiveDataRow(sd, lookups)),
    [fallbackRowsStatusData, lookups],
  );

  const lastKnownRows: LiveDataRow[] = useMemo(
    () =>
      lastKnownStatusDataIsError
        ? NO_ROWS
        : (lastKnownStatusData ?? []).map(sd => toLiveDataRow(sd, lookups)),
    [lastKnownStatusDataIsError, lastKnownStatusData, lookups],
  );

  const gpsOdometerRows: LiveDataRow[] = useMemo(
    () =>
      gpsOdometerStatusDataIsError
        ? NO_ROWS
        : (gpsOdometerStatusData ?? []).map(sd => toLiveDataRow(sd, lookups)),
    [gpsOdometerStatusDataIsError, gpsOdometerStatusData, lookups],
  );

  return {
    rows,
    gridRows,
    fallbackRows,
    fallbackLoading,
    lastKnownRows,
    lastKnownLoading,
    gpsOdometerRows,
    // A name-matched checklist line would be a false miss while names are still resolving.
    diagnosticsLoading,
    isLoading,
    isFetching,
    // Blocking failures only. A failed 24 h history leaves the live rows usable; it is reported
    // separately as fallbackError.
    isError: liveStatusDataIsError || diagnosticsIsError,
    fallbackError: fallbackStatusDataIsError,
  };
}

function sortedDiagnosticIds(...pages: (StatusData[] | undefined)[]): string[] {
  const ids = new Set<string>();

  for (const page of pages) {
    for (const record of page ?? []) {
      const id = record.diagnostic?.id;
      if (id) {
        ids.add(id);
      }
    }
  }

  return [...ids].sort();
}
